// @ts-nocheck
import { useState } from 'react'
import { useQuery, useMutation, useAction } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import type { Id } from '../../../../convex/_generated/dataModel'
import { useAuth } from '@/contexts/auth-context'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  Camera, FlaskConical, Play, Users, TrendingUp,
  ChevronRight, Clock, MessageSquare, Film,
  Plus, Trash2, GitFork, ChevronDown, ChevronUp,
  Loader2, X, Zap, Target, Bot, Copy, ArrowLeft, Paperclip, Image as ImageIcon, FileVideo, FileAudio,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'

type Tab = 'snapshots' | 'personas' | 'runs' | 'trends'

const inputClass = "w-full px-3 py-2 text-sm rounded-lg border border-neutral-300 bg-white text-neutral-900 placeholder:text-neutral-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30 transition-colors"
const textareaClass = `${inputClass} resize-none`
const labelClass = "text-xs font-medium text-neutral-600 block mb-1.5"

function formatTimeAgo(timestamp: number): string {
  const diff = Date.now() - timestamp
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days < 7) return `${days}d ago`
  return new Date(timestamp).toLocaleDateString()
}

function ScoreBar({ score, size = 'sm' }: { score: number; size?: 'sm' | 'lg' }) {
  const pct = Math.round(score * 100)
  const barColor = score >= 0.8 ? 'bg-green-500' : score >= 0.5 ? 'bg-yellow-500' : 'bg-red-500'
  const textColor = score >= 0.8 ? 'text-green-600' : score >= 0.5 ? 'text-yellow-600' : 'text-red-600'
  const barW = size === 'lg' ? 'w-28 h-2' : 'w-20 h-1.5'
  return (
    <div className="flex items-center gap-2">
      <div className={cn("bg-neutral-200 rounded-full overflow-hidden", barW)}>
        <div className={cn("h-full rounded-full transition-all", barColor)} style={{ width: `${pct}%` }} />
      </div>
      <span className={cn("text-xs tabular-nums font-medium", textColor)}>{pct}%</span>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending: 'bg-neutral-100 text-neutral-600',
    running: 'bg-blue-100 text-blue-700',
    judging: 'bg-purple-100 text-purple-700',
    completed: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
  }
  const isActive = status === 'running' || status === 'judging'
  return (
    <span className={cn("inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium", styles[status] ?? styles.pending)}>
      {isActive && <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />}
      {status}
    </span>
  )
}

// ─── Project Browser (for creating snapshots) ───

function ProjectBrowser({ onSelectProject }: { onSelectProject: (id: Id<"workflows">, messageCount: number) => void }) {
  const workflows = useQuery(api.evals.listWorkflowsForEval, { limit: 30 })
  const [search, setSearch] = useState('')

  if (!workflows) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-neutral-400" /></div>

  const filtered = search.trim()
    ? workflows.filter((w) => (w.title || '').toLowerCase().includes(search.toLowerCase()) || (w.userName || '').toLowerCase().includes(search.toLowerCase()))
    : workflows

  return (
    <div className="space-y-3">
      <input
        type="text"
        placeholder="Search videos..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className={inputClass}
        autoFocus
      />
      <div className="space-y-1 max-h-[60vh] overflow-y-auto">
        {filtered.map((w) => (
          <button
            key={w._id}
            onClick={() => onSelectProject(w._id, w.messageCount)}
            className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-neutral-100 text-left transition-colors border border-transparent hover:border-neutral-200"
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{w.title || 'Untitled'}</div>
              <div className="text-xs text-neutral-500 flex items-center gap-3 mt-0.5">
                <span>{w.userName}</span>
                <span className="flex items-center gap-1"><MessageSquare className="h-3 w-3" />{w.messageCount}</span>
                <span className="flex items-center gap-1"><Film className="h-3 w-3" />{w.clipCount}</span>
                <span>{formatTimeAgo(w.updatedAt)}</span>
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-neutral-400 flex-shrink-0" />
          </button>
        ))}
        {filtered.length === 0 && (
          <div className="text-sm text-neutral-500 text-center py-8">{search ? 'No matching videos' : 'No videos found'}</div>
        )}
      </div>
    </div>
  )
}

// ─── Message Inspector (for picking fork point) ───

function MessageInspector({ workflowId, onCreateSnapshot, onFork }: {
  workflowId: Id<"workflows">
  onCreateSnapshot: (workflowId: Id<"workflows">, messageIndex: number) => void
  onFork: (workflowId: Id<"workflows">, messageIndex: number) => void
}) {
  const messages = useQuery(api.evals.getWorkflowMessages, { workflowId })
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [expandedMsg, setExpandedMsg] = useState<number | null>(null)

  if (!messages) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-neutral-400" /></div>

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-neutral-500">{messages.length} messages &mdash; click a message to set fork point</div>
        {selectedIndex !== null && (
          <div className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-200">
            <span className="text-xs text-blue-700 font-medium">Fork after #{selectedIndex}</span>
            <Button size="sm" variant="outline" onClick={() => onFork(workflowId, selectedIndex)}>
              <GitFork className="h-3.5 w-3.5 mr-1" /> Fork
            </Button>
            <Button size="sm" onClick={() => onCreateSnapshot(workflowId, selectedIndex)}>
              <Camera className="h-3.5 w-3.5 mr-1" /> Snapshot
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-1 max-h-[60vh] overflow-y-auto rounded-lg border border-neutral-200 p-2">
        {messages.map((msg, i) => {
          const index = i + 1
          const isSelected = selectedIndex === index
          const isExpanded = expandedMsg === index
          const toolCount = msg.toolCalls?.length ?? 0
          const roleConfig = msg.role === 'user'
            ? { bg: 'bg-blue-500', text: 'text-white', label: 'U' }
            : msg.role === 'assistant'
            ? { bg: 'bg-green-500', text: 'text-white', label: 'A' }
            : { bg: 'bg-neutral-400', text: 'text-white', label: 'S' }

          return (
            <div
              key={msg._id}
              className={cn(
                "px-3 py-2 rounded-lg border cursor-pointer transition-colors",
                isSelected
                  ? "border-blue-500 bg-blue-50"
                  : "border-transparent hover:bg-neutral-50"
              )}
              onClick={() => setSelectedIndex(isSelected ? null : index)}
            >
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono text-neutral-400 w-4 flex-shrink-0">{index}</span>
                <span className={cn("w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0", roleConfig.bg, roleConfig.text)}>
                  {roleConfig.label}
                </span>
                <span className="text-sm truncate flex-1">{msg.content.slice(0, 120) || '(empty)'}</span>
                {toolCount > 0 && (
                  <span className="text-[10px] bg-neutral-200 px-1.5 py-0.5 rounded flex-shrink-0">
                    {toolCount} tool{toolCount !== 1 ? 's' : ''}
                  </span>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); setExpandedMsg(isExpanded ? null : index) }}
                  className="text-neutral-400 hover:text-neutral-600 flex-shrink-0 p-0.5 rounded hover:bg-neutral-100"
                >
                  {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </button>
              </div>

              {isExpanded && (
                <div className="mt-2 pl-11 space-y-2">
                  <pre className="text-xs text-neutral-600 whitespace-pre-wrap max-h-40 overflow-y-auto bg-neutral-50 p-2.5 rounded-lg">
                    {msg.content}
                  </pre>
                  {msg.toolCalls && msg.toolCalls.length > 0 && (
                    <div className="space-y-1">
                      <div className="text-[10px] font-medium text-neutral-500 uppercase tracking-wide">Tool Calls</div>
                      {msg.toolCalls.map((tc: any, j: number) => (
                        <div key={j} className="text-xs font-mono bg-neutral-100 px-2.5 py-1.5 rounded-lg">
                          <span className="text-blue-600">{tc.name}</span>({JSON.stringify(tc.input).slice(0, 80)}...)
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Create Snapshot Dialog ───

function CreateSnapshotPanel({ workflowId, messageIndex, onClose, onCreated }: {
  workflowId: Id<"workflows">
  messageIndex: number
  onClose: () => void
  onCreated: () => void
}) {
  const createSnapshot = useMutation(api.evals.createSnapshot)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [criteriaText, setCriteriaText] = useState('')
  const [loading, setLoading] = useState(false)

  const handleCreate = async () => {
    if (!name.trim()) return
    setLoading(true)
    try {
      const criteria = criteriaText.trim().split('\n').filter(Boolean).map((line, i) => ({
        name: `criterion_${i + 1}`,
        description: line.trim(),
        weight: 1,
        type: "llm" as const,
      }))

      await createSnapshot({
        sourceWorkflowId: workflowId,
        messageIndex,
        name: name.trim(),
        description: description.trim() || undefined,
        criteria: criteria.length > 0 ? criteria : [{
          name: "quality",
          description: "Agent response is helpful and appropriate",
          weight: 1,
          type: "llm" as const,
        }],
      })
      onCreated()
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="rounded-xl border border-neutral-200 overflow-hidden">
      <div className="px-5 py-3.5 border-b border-neutral-200 bg-neutral-50 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Create Snapshot</h3>
          <p className="text-xs text-neutral-500 mt-0.5">Capturing messages 1&ndash;{messageIndex}</p>
        </div>
        <button onClick={onClose} className="p-1 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-200 rounded transition-colors"><X className="h-4 w-4" /></button>
      </div>
      <div className="p-5 space-y-4">

      <div>
        <label className={labelClass}>Snapshot Name <span className="text-red-400">*</span></label>
        <input
          type="text"
          placeholder="e.g. Happy path - 5 clip video"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={inputClass}
        />
      </div>

      <div>
        <label className={labelClass}>Description</label>
        <input
          type="text"
          placeholder="What this snapshot tests"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className={inputClass}
        />
      </div>

      <div>
        <label className={labelClass}>Judge Criteria <span className="text-neutral-400 font-normal">(one per line)</span></label>
        <textarea
          placeholder={"Agent should create at least 3 clips\nResponse should reference the narrative\nTool calls should be well-ordered"}
          value={criteriaText}
          onChange={(e) => setCriteriaText(e.target.value)}
          rows={4}
          className={`${textareaClass} font-mono`}
        />
      </div>

      </div>
      <div className="px-5 py-3 border-t border-neutral-200 bg-neutral-50 flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        <Button size="sm" onClick={handleCreate} disabled={!name.trim() || loading}>
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Camera className="h-3.5 w-3.5 mr-1" />}
          Create Snapshot
        </Button>
      </div>
    </div>
  )
}

// ─── Snapshots Tab ───

function SnapshotsTab() {
  const snapshots = useQuery(api.evals.listSnapshots, {})
  const deleteSnapshot = useMutation(api.evals.deleteSnapshot)
  const triggerEval = useAction(api.evals.triggerSnapshotEval)
  const [runningId, setRunningId] = useState<string | null>(null)
  const [browsing, setBrowsing] = useState(false)
  const [selectedProject, setSelectedProject] = useState<{ id: Id<"workflows">; messageCount: number } | null>(null)
  const [snapshotPanel, setSnapshotPanel] = useState<{ workflowId: Id<"workflows">; messageIndex: number } | null>(null)
  const navigate = useNavigate()

  const handleFork = async (workflowId: Id<"workflows">, _messageIndex: number) => {
    navigate(`/video/${workflowId}`)
  }

  if (snapshotPanel) {
    return (
      <CreateSnapshotPanel
        workflowId={snapshotPanel.workflowId}
        messageIndex={snapshotPanel.messageIndex}
        onClose={() => setSnapshotPanel(null)}
        onCreated={() => { setSnapshotPanel(null); setSelectedProject(null); setBrowsing(false) }}
      />
    )
  }

  if (selectedProject) {
    return (
      <div className="space-y-3">
        <button
          onClick={() => setSelectedProject(null)}
          className="text-sm text-neutral-600 hover:text-neutral-900 flex items-center gap-1.5 py-1 transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to projects
        </button>
        <MessageInspector
          workflowId={selectedProject.id}
          onCreateSnapshot={(wId, idx) => setSnapshotPanel({ workflowId: wId, messageIndex: idx })}
          onFork={handleFork}
        />
      </div>
    )
  }

  if (browsing) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setBrowsing(false)}
            className="text-sm text-neutral-600 hover:text-neutral-900 flex items-center gap-1.5 py-1 transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to snapshots
          </button>
        </div>
        <ProjectBrowser onSelectProject={(id, mc) => setSelectedProject({ id, messageCount: mc })} />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-neutral-500">{snapshots?.length ?? 0} snapshots</div>
        <Button size="sm" onClick={() => setBrowsing(true)}>
          <Plus className="h-3.5 w-3.5 mr-1" /> New Snapshot
        </Button>
      </div>

      {!snapshots ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-neutral-400" /></div>
      ) : snapshots.length === 0 ? (
        <div className="text-center py-16 text-neutral-500">
          <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-3">
            <Camera className="h-6 w-6 opacity-50" />
          </div>
          <div className="text-sm font-medium">No snapshots yet</div>
          <div className="text-xs mt-2 max-w-sm mx-auto space-y-1 text-left">
            <div className="flex items-start gap-2"><span className="w-4 h-4 rounded-full bg-blue-100 text-blue-600 text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">1</span><span>Browse a video with agent conversation</span></div>
            <div className="flex items-start gap-2"><span className="w-4 h-4 rounded-full bg-blue-100 text-blue-600 text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">2</span><span>Select a fork point in the message history</span></div>
            <div className="flex items-start gap-2"><span className="w-4 h-4 rounded-full bg-blue-100 text-blue-600 text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">3</span><span>Define judge criteria for scoring</span></div>
          </div>
          <Button size="sm" variant="outline" className="mt-4" onClick={() => setBrowsing(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Browse Videos
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {snapshots.map((s) => (
            <div key={s._id} className="rounded-lg border border-neutral-200 hover:bg-neutral-50 transition-colors">
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium">{s.name}</div>
                    {s.tags?.map((t: string) => (
                      <span key={t} className="text-[10px] bg-neutral-100 px-1.5 py-0.5 rounded font-medium">{t}</span>
                    ))}
                  </div>
                  <div className="text-xs text-neutral-500 flex items-center gap-3 mt-1">
                    <span className="flex items-center gap-1"><Film className="h-3 w-3" />{s.sourceTitle}</span>
                    <span className="flex items-center gap-1"><MessageSquare className="h-3 w-3" />{s.messageIndex} msgs</span>
                    <span>{s.criteria.length} criteria</span>
                    {s.runCount > 0 && <span>{s.runCount} runs</span>}
                  </div>
                  {s.lastScore !== undefined && (
                    <div className="mt-1.5"><ScoreBar score={s.lastScore} /></div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={runningId === s._id}
                    onClick={async (e) => {
                      e.stopPropagation()
                      setRunningId(s._id)
                      try {
                        await triggerEval({ snapshotId: s._id })
                      } finally {
                        setRunningId(null)
                      }
                    }}
                  >
                    {runningId === s._id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Play className="h-3.5 w-3.5 mr-1" />}
                    Run
                  </Button>
                  <button
                    onClick={() => deleteSnapshot({ id: s._id })}
                    className="p-1.5 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Personas Tab ───

const styleColors: Record<string, { className: string; label: string }> = {
  terse: { className: 'bg-orange-100 text-orange-700', label: 'Terse' },
  balanced: { className: 'bg-blue-100 text-blue-700', label: 'Balanced' },
  verbose: { className: 'bg-purple-100 text-purple-700', label: 'Verbose' },
  creative: { className: 'bg-pink-100 text-pink-700', label: 'Creative' },
  indecisive: { className: 'bg-yellow-100 text-yellow-700', label: 'Indecisive' },
}

function PersonasTab() {
  const personas = useQuery(api.evals.listPersonas, {})
  const createPersona = useMutation(api.evals.createPersona)
  const deletePersona = useMutation(api.evals.deletePersona)
  const triggerE2E = useAction(api.evals.triggerE2EEval)
  const generateUploadUrl = useMutation(api.workflows.generateUploadUrl)
  const getStorageUrl = useMutation(api.workflows.getStorageUrl)
  const [creating, setCreating] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [runningId, setRunningId] = useState<string | null>(null)
  const [form, setForm] = useState({
    name: '',
    description: '',
    systemPrompt: '',
    initialMessage: '',
    goals: '',
    interactionStyle: 'balanced',
  })
  const [mediaAssets, setMediaAssets] = useState<Array<{ type: 'image' | 'video' | 'audio'; url: string; name: string }>>([])
  const [uploading, setUploading] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    setUploading(true)
    try {
      const newAssets: typeof mediaAssets = []
      for (const file of Array.from(files)) {
        const uploadUrl = await generateUploadUrl()
        const result = await fetch(uploadUrl, { method: 'POST', headers: { 'Content-Type': file.type }, body: file })
        const { storageId } = await result.json()
        const url = await getStorageUrl({ storageId })
        if (!url) continue
        const type = file.type.startsWith('video/') ? 'video' as const : file.type.startsWith('audio/') ? 'audio' as const : 'image' as const
        newAssets.push({ type, url, name: file.name })
      }
      setMediaAssets((prev) => [...prev, ...newAssets])
    } finally {
      setUploading(false)
    }
  }

  const handleDuplicate = async (p: any) => {
    await createPersona({
      name: `${p.name} (copy)`,
      description: p.description,
      systemPrompt: p.systemPrompt,
      initialMessage: p.initialMessage,
      goals: p.goals,
      interactionStyle: p.interactionStyle ?? 'balanced',
      mediaAssets: p.mediaAssets,
    })
  }

  const handleCreate = async () => {
    if (!form.name.trim()) return
    setLoading(true)
    try {
      await createPersona({
        name: form.name.trim(),
        description: form.description.trim(),
        systemPrompt: form.systemPrompt.trim() || `You are a user who wants to create a video project. Interact naturally and give creative direction.`,
        initialMessage: form.initialMessage.trim() || 'I want to create a video project',
        goals: form.goals.split('\n').filter(Boolean).map((g) => g.trim()),
        interactionStyle: form.interactionStyle,
        mediaAssets: mediaAssets.length > 0 ? mediaAssets : undefined,
      })
      setForm({ name: '', description: '', systemPrompt: '', initialMessage: '', goals: '', interactionStyle: 'balanced' })
      setMediaAssets([])
      setCreating(false)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-neutral-500">{personas?.length ?? 0} personas</div>
        <Button size="sm" onClick={() => setCreating(!creating)}>
          {creating ? <X className="h-3.5 w-3.5 mr-1" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
          {creating ? 'Cancel' : 'New Persona'}
        </Button>
      </div>

      {creating && (
        <div className="rounded-xl border border-neutral-200 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-neutral-200 bg-neutral-50">
            <h3 className="text-sm font-semibold">New Persona</h3>
            <p className="text-xs text-neutral-500 mt-0.5">Define a synthetic user for end-to-end simulation evals</p>
          </div>
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Name <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  placeholder="e.g. Product Video Creator"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Interaction Style</label>
                <select
                  value={form.interactionStyle}
                  onChange={(e) => setForm({ ...form, interactionStyle: e.target.value })}
                  className={inputClass}
                >
                  <option value="terse">Terse — short, direct</option>
                  <option value="balanced">Balanced</option>
                  <option value="verbose">Verbose — detailed</option>
                  <option value="creative">Creative — lots of ideas</option>
                  <option value="indecisive">Indecisive — needs guidance</option>
                </select>
              </div>
            </div>
            <div>
              <label className={labelClass}>Description</label>
              <input
                type="text"
                placeholder="Short description of this persona's behavior"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>System Prompt</label>
              <textarea
                placeholder="Instructions for the user simulator LLM..."
                value={form.systemPrompt}
                onChange={(e) => setForm({ ...form, systemPrompt: e.target.value })}
                rows={4}
                className={`${textareaClass} font-mono`}
              />
              <p className="text-[10px] text-neutral-400 mt-1">Leave empty for default: natural video creator</p>
            </div>
            <div>
              <label className={labelClass}>Initial Message</label>
              <input
                type="text"
                placeholder="First message sent to the agent"
                value={form.initialMessage}
                onChange={(e) => setForm({ ...form, initialMessage: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Goals <span className="text-neutral-400 font-normal">(one per line)</span></label>
              <textarea
                placeholder={"Create a 5-clip product video\nAdd background music\nExport final video"}
                value={form.goals}
                onChange={(e) => setForm({ ...form, goals: e.target.value })}
                rows={3}
                className={`${textareaClass} font-mono`}
              />
            </div>

            <div>
              <label className={labelClass}>Media Assets <span className="font-normal text-neutral-400">(images, videos, or audio the persona can attach)</span></label>
              {mediaAssets.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {mediaAssets.map((asset, i) => (
                    <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-neutral-50 border border-neutral-200 text-xs">
                      {asset.type === 'video' ? <FileVideo className="h-3.5 w-3.5 text-blue-500" /> : asset.type === 'audio' ? <FileAudio className="h-3.5 w-3.5 text-purple-500" /> : <ImageIcon className="h-3.5 w-3.5 text-green-500" />}
                      <span className="text-neutral-700 max-w-[120px] truncate">{asset.name}</span>
                      <button
                        onClick={() => setMediaAssets((prev) => prev.filter((_, j) => j !== i))}
                        className="text-neutral-400 hover:text-red-500"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <label className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-neutral-300 hover:border-blue-400 hover:bg-blue-50/50 cursor-pointer transition-colors">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin text-neutral-400" /> : <Paperclip className="h-4 w-4 text-neutral-400" />}
                <span className="text-sm text-neutral-500">{uploading ? 'Uploading...' : 'Attach files'}</span>
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*,audio/*"
                  onChange={(e) => handleFileUpload(e.target.files)}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            </div>
          </div>
          <div className="px-5 py-3 border-t border-neutral-200 bg-neutral-50 flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setCreating(false)}>Cancel</Button>
            <Button size="sm" onClick={handleCreate} disabled={!form.name.trim() || loading}>
              {loading && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
              Create Persona
            </Button>
          </div>
        </div>
      )}

      {!personas ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-neutral-400" /></div>
      ) : personas.length === 0 && !creating ? (
        <div className="text-center py-16 text-neutral-500">
          <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-3">
            <Users className="h-6 w-6 opacity-50" />
          </div>
          <div className="text-sm font-medium">No personas defined</div>
          <div className="text-xs mt-1 max-w-xs mx-auto">Personas are synthetic users that simulate real behavior during end-to-end evals</div>
          <Button size="sm" variant="outline" className="mt-4" onClick={() => setCreating(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Create First Persona
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {personas?.map((p) => {
            const isExpanded = expandedId === p._id
            const style = styleColors[p.interactionStyle ?? 'balanced'] ?? styleColors.balanced
            return (
              <div key={p._id} className="rounded-lg border border-neutral-200 overflow-hidden">
                <button
                  onClick={() => setExpandedId(isExpanded ? null : p._id)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-neutral-50 text-left transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center flex-shrink-0">
                      <Bot className="h-4 w-4 text-neutral-500" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{p.name}</div>
                      <div className="text-xs text-neutral-500 truncate">{p.description} &middot; {p.goals.length} goal{p.goals.length !== 1 ? 's' : ''}{p.mediaAssets?.length > 0 && <> &middot; <Paperclip className="h-3 w-3 inline" /> {p.mediaAssets.length} file{p.mediaAssets.length !== 1 ? 's' : ''}</>}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium", style.className)}>
                      {style.label}
                    </span>
                    <span className="text-[10px] text-neutral-400">{p.goals.length} goals</span>
                    {isExpanded ? <ChevronUp className="h-3.5 w-3.5 text-neutral-400" /> : <ChevronDown className="h-3.5 w-3.5 text-neutral-400" />}
                  </div>
                </button>
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-neutral-100 space-y-3">
                    <div className="pt-3">
                      <div className="text-[10px] font-medium text-neutral-500 uppercase tracking-wide mb-1.5">System Prompt</div>
                      <pre className="text-xs text-neutral-600 whitespace-pre-wrap bg-neutral-50 p-3 rounded-lg font-mono max-h-32 overflow-y-auto">{p.systemPrompt}</pre>
                    </div>
                    <div>
                      <div className="text-[10px] font-medium text-neutral-500 uppercase tracking-wide mb-1.5">Initial Message</div>
                      <div className="text-xs text-neutral-600 bg-neutral-50 p-3 rounded-lg">{p.initialMessage}</div>
                    </div>
                    {p.goals.length > 0 && (
                      <div>
                        <div className="text-[10px] font-medium text-neutral-500 uppercase tracking-wide mb-1.5">Goals</div>
                        <div className="flex flex-wrap gap-1.5">
                          {p.goals.map((g: string, i: number) => (
                            <span key={i} className="inline-flex items-center gap-1 text-xs bg-neutral-100 text-neutral-700 px-2 py-1 rounded-full">
                              <Target className="h-3 w-3 text-neutral-400" />{g}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {p.mediaAssets && p.mediaAssets.length > 0 && (
                      <div>
                        <div className="text-[10px] font-medium text-neutral-500 uppercase tracking-wide mb-1.5">Media Assets</div>
                        <div className="flex flex-wrap gap-2">
                          {p.mediaAssets.map((asset: any, i: number) => (
                            <a key={i} href={asset.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-neutral-50 border border-neutral-200 text-xs hover:border-blue-300 transition-colors">
                              {asset.type === 'video' ? <FileVideo className="h-3.5 w-3.5 text-blue-500" /> : asset.type === 'audio' ? <FileAudio className="h-3.5 w-3.5 text-purple-500" /> : <ImageIcon className="h-3.5 w-3.5 text-green-500" />}
                              <span className="text-neutral-700 max-w-[150px] truncate">{asset.name || asset.type}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-2 pt-2 border-t border-neutral-100">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={runningId === p._id}
                        onClick={async (e) => {
                          e.stopPropagation()
                          setRunningId(p._id)
                          try { await triggerE2E({ personaId: p._id }) } finally { setRunningId(null) }
                        }}
                      >
                        {runningId === p._id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Zap className="h-3.5 w-3.5 mr-1" />}
                        Run E2E Eval
                      </Button>
                      <button
                        onClick={() => handleDuplicate(p)}
                        className="p-1.5 text-neutral-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors"
                        title="Duplicate persona"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                      <div className="flex-1" />
                      <button
                        onClick={() => deletePersona({ id: p._id })}
                        className="p-1.5 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        title="Delete persona"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── Runs Tab ───

type RunFilter = 'all' | 'pending' | 'running' | 'judging' | 'completed' | 'failed'
type RunSort = 'newest' | 'oldest' | 'highest' | 'lowest'

function RunDetailPanel({ run }: { run: any }) {
  const detail = useQuery(api.evals.getEvalRunDetail, run.forkWorkflowId ? { runId: run._id } : "skip")
  const [logTab, setLogTab] = useState<'scores' | 'response' | 'tools' | 'conversation' | 'video'>('scores')
  const messages = detail?.messages ?? run.conversationLog ?? []

  return (
    <div className="border-t border-neutral-200">
      {run.error && (
        <div className="mx-4 mt-3 text-xs text-red-600 font-mono bg-red-50 p-3 rounded-lg border border-red-200">{run.error}</div>
      )}

      {/* Action bar: project link */}
      {detail?.shortcode && (
        <div className="flex items-center gap-2 px-4 pt-3">
          <a
            href={`/video/${detail.shortcode}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
          >
            <GitFork className="h-3 w-3" />
            Open Project
            <span className="font-mono opacity-60">{detail.shortcode}</span>
          </a>
          {detail.aspectRatio && (
            <span className="text-[10px] bg-neutral-100 px-1.5 py-0.5 rounded font-medium">{detail.aspectRatio}</span>
          )}
          {detail.completedClipCount !== undefined && (
            <span className="text-[10px] text-neutral-500">{detail.completedClipCount}/{detail.clipCount} clips</span>
          )}
        </div>
      )}

      <div className="flex gap-1 px-4 pt-3">
        {(['scores', 'conversation', 'video', 'response', 'tools'] as const).map((t) => {
          const hasContent = t === 'scores' ? run.scores?.length
            : t === 'response' ? run.agentResponse
            : t === 'tools' ? run.agentToolCalls?.length
            : t === 'conversation' ? (messages.length > 0)
            : t === 'video' ? (detail?.exportVideoUrl || detail?.completedClipCount)
            : false
          if (!hasContent) return null
          return (
            <button
              key={t}
              onClick={() => setLogTab(t)}
              className={cn(
                "px-3 py-1.5 text-xs font-medium rounded-t-lg transition-colors",
                logTab === t
                  ? "bg-neutral-100 text-neutral-900"
                  : "text-neutral-500 hover:text-neutral-700"
              )}
            >
              {t === 'video' ? 'Video' : t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          )
        })}
      </div>

      <div className="px-4 pb-4">
        <div className="bg-neutral-50 rounded-lg p-3">
          {logTab === 'scores' && run.scores && run.scores.length > 0 && (
            <div className="space-y-2">
              {run.scores.map((s: any, i: number) => (
                <div key={i} className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-neutral-700">{s.criterion}</div>
                    {s.reasoning && <div className="text-[10px] text-neutral-500 mt-0.5 leading-relaxed">{s.reasoning}</div>}
                  </div>
                  <div className="flex-shrink-0">
                    <ScoreBar score={s.score} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {logTab === 'conversation' && messages.length > 0 && (
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {messages.map((msg: any, i: number) => (
                <div key={i} className={cn(
                  "rounded-lg px-3 py-2 text-xs max-w-[85%]",
                  msg.role === 'user'
                    ? "bg-blue-100 text-blue-900 ml-auto"
                    : "bg-white text-neutral-800 border border-neutral-200"
                )}>
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[10px] font-medium opacity-60">{msg.role === 'user' ? 'Simulated User' : 'Agent'}</span>
                    {msg.createdAt && <span className="text-[10px] opacity-40">{new Date(msg.createdAt).toLocaleTimeString()}</span>}
                  </div>
                  {msg.content && <div className="whitespace-pre-wrap">{msg.content}</div>}
                  {msg.toolCalls && msg.toolCalls.length > 0 && (
                    <div className="mt-1 pt-1 border-t border-neutral-200/50 space-y-0.5">
                      {msg.toolCalls.map((tc: any, j: number) => (
                        <div key={j} className="flex items-center gap-1.5 text-[10px] text-neutral-500">
                          <Zap className="h-2.5 w-2.5" />
                          <span className="font-mono">{tc.name}</span>
                          {tc.input && <span className="opacity-60 truncate max-w-[200px]">{JSON.stringify(tc.input).slice(0, 80)}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {logTab === 'video' && (
            <div className="space-y-3">
              {detail?.exportVideoUrl ? (
                <video
                  src={detail.exportVideoUrl}
                  controls
                  className="w-full max-w-sm mx-auto rounded-lg"
                  style={{ aspectRatio: detail.aspectRatio === '9:16' ? '9/16' : detail.aspectRatio === '1:1' ? '1/1' : '16/9' }}
                />
              ) : (
                <div className="text-center py-6 text-neutral-500 text-xs">
                  No export video available
                  {detail?.completedClipCount ? ` (${detail.completedClipCount} clips with video)` : ''}
                </div>
              )}
            </div>
          )}

          {logTab === 'response' && run.agentResponse && (
            <pre className="text-xs text-neutral-600 whitespace-pre-wrap max-h-60 overflow-y-auto font-mono">
              {run.agentResponse}
            </pre>
          )}

          {logTab === 'tools' && run.agentToolCalls && (
            <div className="space-y-1.5">
              {(run.agentToolCalls as any[]).map((tc: any, i: number) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className="w-5 h-5 rounded bg-neutral-200 flex items-center justify-center text-[10px] font-mono text-neutral-500 flex-shrink-0">{i + 1}</span>
                  <span className="font-mono font-medium text-neutral-700">{tc.name}</span>
                  {tc.input && <span className="text-neutral-400 truncate">{JSON.stringify(tc.input).slice(0, 60)}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function RunsTab() {
  const runs = useQuery(api.evals.listRuns, {})
  const [expandedRun, setExpandedRun] = useState<string | null>(null)
  const [filter, setFilter] = useState<RunFilter>('all')
  const [sort, setSort] = useState<RunSort>('newest')

  const filteredRuns = runs?.filter((r) => filter === 'all' || r.status === filter)
    .sort((a, b) => {
      if (sort === 'newest') return b.createdAt - a.createdAt
      if (sort === 'oldest') return a.createdAt - b.createdAt
      if (sort === 'highest') return (b.overallScore ?? 0) - (a.overallScore ?? 0)
      return (a.overallScore ?? 0) - (b.overallScore ?? 0)
    })

  const statusCounts = runs?.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          {(['all', 'completed', 'running', 'judging', 'pending', 'failed'] as RunFilter[]).map((f) => {
            const count = f === 'all' ? runs?.length : statusCounts?.[f]
            if (f !== 'all' && !count) return null
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={cn(
                  "px-2.5 py-1 rounded-full text-xs font-medium transition-colors",
                  filter === f
                    ? "bg-blue-100 text-blue-700"
                    : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
                )}
              >
                {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                {count !== undefined && <span className="ml-1 opacity-60">{count}</span>}
              </button>
            )
          })}
        </div>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as RunSort)}
          className="px-2 py-1 text-xs rounded-lg border border-neutral-200 bg-white text-neutral-700"
        >
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="highest">Highest score</option>
          <option value="lowest">Lowest score</option>
        </select>
      </div>

      {!runs ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-neutral-400" /></div>
      ) : filteredRuns && filteredRuns.length === 0 ? (
        <div className="text-center py-16 text-neutral-500">
          <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-3">
            <Play className="h-6 w-6 opacity-50" />
          </div>
          <div className="text-sm font-medium">{filter === 'all' ? 'No eval runs yet' : `No ${filter} runs`}</div>
          <div className="text-xs mt-1">{filter === 'all' ? 'Create a snapshot or persona and run your first eval' : 'Try a different filter'}</div>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredRuns?.map((r) => {
            const isExpanded = expandedRun === r._id
            return (
              <div key={r._id} className="border border-neutral-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpandedRun(isExpanded ? null : r._id)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-neutral-50 text-left transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <StatusBadge status={r.status} />
                    <div className="min-w-0">
                      <span className="text-sm font-medium truncate block">
                        {r.type === 'snapshot' ? r.snapshotName : r.personaName}
                      </span>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] bg-neutral-100 px-1.5 py-0.5 rounded uppercase font-medium">
                          {r.type}
                        </span>
                        <span className="text-[10px] bg-neutral-100 px-1.5 py-0.5 rounded uppercase font-medium">
                          {r.executionMode}
                        </span>
                        {r.agentModel && (
                          <span className="text-[10px] text-neutral-500">{r.agentModel}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {r.overallScore !== undefined && <ScoreBar score={r.overallScore} />}
                    <div className="text-right">
                      <span className="text-xs text-neutral-500 block">{formatTimeAgo(r.createdAt)}</span>
                      {r.totalDurationMs && (
                        <span className="text-[10px] text-neutral-400 flex items-center gap-0.5 justify-end">
                          <Clock className="h-2.5 w-2.5" /> {(r.totalDurationMs / 1000).toFixed(1)}s
                        </span>
                      )}
                    </div>
                    {isExpanded ? <ChevronUp className="h-3.5 w-3.5 text-neutral-400" /> : <ChevronDown className="h-3.5 w-3.5 text-neutral-400" />}
                  </div>
                </button>

                {isExpanded && <RunDetailPanel run={r} />}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── Trends Tab ───

function TrendChart({ data }: { data: Array<{ score: number; createdAt: number }> }) {
  if (data.length < 2) return null
  const maxScore = Math.max(...data.map((d) => d.score), 0.01)
  const w = 600
  const h = 120
  const pad = { top: 10, right: 10, bottom: 20, left: 30 }
  const plotW = w - pad.left - pad.right
  const plotH = h - pad.top - pad.bottom

  const points = data.map((d, i) => ({
    x: pad.left + (i / (data.length - 1)) * plotW,
    y: pad.top + plotH - (d.score / maxScore) * plotH,
    score: d.score,
    date: new Date(d.createdAt).toLocaleDateString(),
  }))

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
  const avg = data.reduce((s, d) => s + d.score, 0) / data.length
  const avgY = pad.top + plotH - (avg / maxScore) * plotH

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ maxHeight: 150 }}>
      <line x1={pad.left} y1={avgY} x2={w - pad.right} y2={avgY} stroke="currentColor" strokeDasharray="4 4" className="text-neutral-300" />
      <text x={pad.left - 4} y={avgY + 3} textAnchor="end" className="text-neutral-400 fill-current" style={{ fontSize: 9 }}>
        {Math.round(avg * 100)}%
      </text>
      <path d={pathD} fill="none" stroke="currentColor" strokeWidth={2} className="text-blue-500" />
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r={3} className="fill-blue-500" />
          <title>{p.date}: {Math.round(p.score * 100)}%</title>
        </g>
      ))}
      <text x={pad.left} y={h - 2} className="text-neutral-400 fill-current" style={{ fontSize: 8 }}>
        {points[0]?.date}
      </text>
      <text x={w - pad.right} y={h - 2} textAnchor="end" className="text-neutral-400 fill-current" style={{ fontSize: 8 }}>
        {points[points.length - 1]?.date}
      </text>
    </svg>
  )
}

function TrendsTab() {
  const snapshots = useQuery(api.evals.listSnapshots, {})
  const personas = useQuery(api.evals.listPersonas, {})
  const [selectedType, setSelectedType] = useState<'snapshot' | 'persona'>('snapshot')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const snapshotTrend = useQuery(
    api.evals.getSnapshotTrend,
    selectedType === 'snapshot' && selectedId ? { snapshotId: selectedId as any, limit: 20 } : "skip"
  )
  const personaTrend = useQuery(
    api.evals.getPersonaTrend,
    selectedType === 'persona' && selectedId ? { personaId: selectedId as any, limit: 20 } : "skip"
  )
  const trendData = selectedType === 'snapshot' ? snapshotTrend : personaTrend

  const items = selectedType === 'snapshot'
    ? (snapshots ?? []).map((s: any) => ({ id: s._id, name: s.name, runCount: s.runCount }))
    : (personas ?? []).map((p: any) => ({ id: p._id, name: p.name, runCount: p.runCount }))

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex rounded-lg border border-neutral-200 overflow-hidden">
          {(['snapshot', 'persona'] as const).map((t) => (
            <button
              key={t}
              onClick={() => { setSelectedType(t); setSelectedId(null) }}
              className={cn(
                "px-3 py-1.5 text-xs font-medium transition-colors",
                selectedType === t ? "bg-blue-100 text-blue-700" : "text-neutral-500 hover:bg-neutral-50"
              )}
            >
              {t === 'snapshot' ? 'Snapshots' : 'Personas'}
            </button>
          ))}
        </div>
        <select
          value={selectedId ?? ''}
          onChange={(e) => setSelectedId(e.target.value || null)}
          className={inputClass}
          style={{ maxWidth: 320 }}
        >
          <option value="">Select {selectedType === 'snapshot' ? 'a snapshot' : 'a persona'}...</option>
          {items.map((item) => (
            <option key={item.id} value={item.id}>{item.name} ({item.runCount} runs)</option>
          ))}
        </select>
      </div>

      {!selectedId ? (
        <div className="text-center py-16 text-neutral-500">
          <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-3">
            <TrendingUp className="h-6 w-6 opacity-50" />
          </div>
          <div className="text-sm font-medium">Select a {selectedType} to view score trends</div>
          <div className="text-xs mt-1">Track how agent performance changes over time</div>
        </div>
      ) : !trendData ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-neutral-400" /></div>
      ) : trendData.length === 0 ? (
        <div className="text-center py-8 text-neutral-500 text-sm">No completed runs yet</div>
      ) : (
        <div className="space-y-4">
          <div className="border border-neutral-200 rounded-xl p-5">
            <div className="text-xs font-medium text-neutral-500 mb-3">Overall Score over {trendData.length} runs</div>
            <TrendChart data={trendData} />
          </div>

          <div className="space-y-1.5">
            {trendData.slice().reverse().map((r: any, idx: number) => {
              const prevIdx = trendData.indexOf(r)
              const delta = prevIdx > 0 ? r.score - trendData[prevIdx - 1].score : 0
              return (
                <div key={r._id} className="flex items-center justify-between px-4 py-2.5 text-sm border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-mono text-neutral-400 w-4">#{trendData.length - idx}</span>
                    <ScoreBar score={r.score} />
                    {delta !== 0 && (
                      <span className={cn(
                        "text-xs font-mono px-1.5 py-0.5 rounded",
                        delta > 0 ? "text-green-700 bg-green-50" : "text-red-700 bg-red-50"
                      )}>
                        {delta > 0 ? '+' : ''}{Math.round(delta * 100)}%
                      </span>
                    )}
                    {r.runLabel && <span className="text-[10px] bg-neutral-100 px-1.5 py-0.5 rounded font-medium">{r.runLabel}</span>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-neutral-500">
                    {r.agentModel && <span className="font-mono">{r.agentModel}</span>}
                    {r.totalDurationMs && <span className="flex items-center gap-0.5"><Clock className="h-3 w-3" />{(r.totalDurationMs / 1000).toFixed(1)}s</span>}
                    <span>{formatTimeAgo(r.createdAt)}</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Main Page ───

function TabBadge({ count }: { count?: number }) {
  if (count === undefined || count === 0) return null
  return (
    <span className="ml-1.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-neutral-200 text-[10px] font-medium tabular-nums">
      {count}
    </span>
  )
}

export default function AdminEvalsPage() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin
  const [tab, setTab] = useState<Tab>('snapshots')
  const snapshots = useQuery(api.evals.listSnapshots, isAdmin ? {} : "skip")
  const personas = useQuery(api.evals.listPersonas, isAdmin ? {} : "skip")
  const runs = useQuery(api.evals.listRuns, isAdmin ? {} : "skip")

  if (!isAdmin) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center">
        <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-4">
          <FlaskConical className="h-6 w-6 text-neutral-400" />
        </div>
        <div className="text-sm font-medium text-neutral-600">Admin access required</div>
        <div className="text-xs text-neutral-500 mt-1">You need admin privileges to view the evals dashboard</div>
      </div>
    )
  }

  const tabCounts: Record<Tab, number | undefined> = {
    snapshots: snapshots?.length,
    personas: personas?.length,
    runs: runs?.length,
    trends: undefined,
  }

  const tabs: { id: Tab; label: string; icon: typeof FlaskConical }[] = [
    { id: 'snapshots', label: 'Snapshots', icon: Camera },
    { id: 'personas', label: 'Personas', icon: Users },
    { id: 'runs', label: 'Runs', icon: Play },
    { id: 'trends', label: 'Trends', icon: TrendingUp },
  ]

  const completedRuns = runs?.filter((r) => r.status === 'completed') ?? []
  const avgScore = completedRuns.length > 0
    ? completedRuns.reduce((sum, r) => sum + (r.overallScore ?? 0), 0) / completedRuns.length
    : undefined
  const runningCount = runs?.filter((r) => r.status === 'running' || r.status === 'judging').length ?? 0

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
            <FlaskConical className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold">Evals</h1>
            <p className="text-xs text-neutral-500">Test agent behavior with snapshots and simulated users</p>
          </div>
        </div>

        {(snapshots?.length || personas?.length || runs?.length) ? (
          <div className="flex items-center gap-4 text-xs text-neutral-500">
            {avgScore !== undefined && (
              <div className="flex items-center gap-1.5">
                <span>Avg score</span>
                <ScoreBar score={avgScore} />
              </div>
            )}
            {runningCount > 0 && (
              <div className="flex items-center gap-1.5 text-blue-600">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>{runningCount} running</span>
              </div>
            )}
          </div>
        ) : null}
      </div>

      <div className="flex gap-1 mb-6 border-b border-neutral-200">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              "flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors",
              tab === t.id
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-neutral-500 hover:text-neutral-700"
            )}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
            <TabBadge count={tabCounts[t.id]} />
          </button>
        ))}
      </div>

      {tab === 'snapshots' && <SnapshotsTab />}
      {tab === 'personas' && <PersonasTab />}
      {tab === 'runs' && <RunsTab />}
      {tab === 'trends' && <TrendsTab />}
    </div>
  )
}
